<template>
  <div class="flex flex-col gap-4 w-full">
    <!-- Quick Date Presets -->
    <FilterSingleDayQuickDates v-if="useSingleDayDatePicker" ref="singleDayQuickDates" v-model="selectedPreset"
      @change="handleSingleDayPresetChange" />
    <FilterQuickDates v-else ref="quickDates" v-model="selectedPreset" @change="handlePresetChange" />

    <!-- Main Filters Form -->
    <div class="flex flex-col lg:flex-row justify-between gap-4 w-full">
      <v-form ref="form" lazy-validation v-model="valid" class="flex-1 w-full">
        <div class="flex items-center w-full flex-wrap gap-2">
          <!-- Report Type Select -->
          <FilterSelect v-model="selectedReport" :options="selectReport" :label="$t('reports.filters.report_type')"
            id="report-options" bg-color="primary" @change="handleReportChange" />

          <!-- Date Range (normal or single-day) -->
          <FilterSingleDayDateRange v-if="useSingleDayDatePicker" ref="singleDayDateRange" v-model="selectedPreset"
            v-model:from="frDate" v-model:to="toDate" @change="handleDateChange" />
          <FilterDateRange v-else v-model:from="frDate" v-model:to="toDate" @change="handleDateChange" />

          <!-- Duration Filter -->
          <FilterDuration v-if="filterDuration" v-model="duration" @change="handleDurationChange" />

          <!-- Call Type Filter -->
          <FilterSelect v-if="filterCallType" v-model="selectedCallType" :options="callTypeOptions"
            :label="$t('reports.filters.call_type')" id="call-options"
            :all-option="{ value: 0, label: $t('reports.filters.all') }" @change="handleCallTypeChange" />

          <!-- Agent Filter -->
          <FilterSelect v-if="filterAgent" v-model="selectedAgent" :options="agentOptions"
            :label="$t('reports.filters.select_agent')" id="agent-options"
            :all-option="{ value: 0, label: $t('reports.filters.all') }" :filter="true" @change="handleAgentChange" />

          <!-- Hung Party Filter -->
          <FilterSelect v-if="filterHungparty" v-model="selectedHaung" :options="hungPartyOptions"
            :label="$t('reports.filters.hung_party')" id="hung-options"
            :all-option="{ value: 0, label: $t('reports.filters.all') }" @change="handleHungPartyChange" />

          <!-- Number Filter -->
          <FilterInput v-if="filterNumber" v-model="numberText" :label="$t('reports.filters.number')" id="number"
            @change="handleNumberChange" />

          <!-- Skill Filter -->
          <FilterSelect v-if="filterSkills" v-model="selectedSkill" :options="skillOptions"
            :label="$t('reports.filters.select_skill')" id="skill-options"
            :all-option="{ value: 0, label: $t('reports.filters.all') }" @change="handleSkillChange" />

          <!-- Period Filter -->
          <FilterSelect v-if="filterPeriods" v-model="selectedPeriod" :options="periods"
            :label="$t('reports.filters.select_period')" id="period-options" @change="handlePeriodChange" />

          <!-- Agent Status Filter -->
          <FilterSelect v-if="filterAgentStatus" v-model="selectedAgentStatus" :options="agentStatusOptions"
            :label="$t('reports.filters.select_status')" id="status-options"
            :all-option="{ value: 0, label: $t('reports.filters.all') }" @change="handleAgentStatusChange" />

          <!-- Omni Channel Filter -->
          <FilterOmniSelect v-if="filterOmni" v-model="selectedOmniType" @change="handleOmniChange" />

          <!-- Report Variant Filter (for CSAT Table Report) -->
          <FilterSelect v-if="filterReportVariant" v-model="selectedReportVariant" :options="reportVariantOptions"
            :label="$t('reports.csat_report.report_variant')" id="report-variant-options"
            @change="handleReportVariantChange" />

        </div>
      </v-form>

      <!-- Action Buttons -->
      <FilterActions :loading="loading" :loading-export="loadingExport" :is-valid="enableBtns" :hide-export="NoExport"
        :enable-btns="enableBtns" @get="submit" @export="submit(_, true)" />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import {
  FilterSelect,
  FilterDateRange,
  FilterDuration,
  FilterInput,
  FilterOmniSelect,
  FilterActions,
  FilterQuickDates,
} from "./filters/index.js";
import FilterSingleDayDateRange from "./filters/FilterSingleDayDateRange.vue";
import FilterSingleDayQuickDates from "./filters/FilterSingleDayQuickDates.vue";

export default {
  name: "report-filters-form",
  components: {
    FilterSelect,
    FilterDateRange,
    FilterSingleDayDateRange,
    FilterSingleDayQuickDates,
    FilterDuration,
    FilterInput,
    FilterOmniSelect,
    FilterActions,
    FilterQuickDates,
  },
  props: {
    showCallType: Boolean,
    showAgent: Boolean,
    showHungparty: Boolean,
    showNumber: Boolean,
    showDuration: Boolean,
    showSkill: Boolean,
    showPeriods: Boolean,
    showAgentStatus: Boolean,
    hideExport: Boolean,
    showOmni: Boolean,
    showReportVariant: Boolean,
    useSingleDayDatePicker: {
      type: Boolean,
      default: false,
    },
    agents: Array,
    skills: Array,
  },
  emits: ["submitReport", "report_type", "skillChanged"],
  data() {
    return {
      valid: true,
      selectedPreset: "today",
      frDate: moment().startOf("day").format("YYYY-MM-DD HH:mm:ss").toString(),
      toDate: moment().endOf("day").format("YYYY-MM-DD HH:mm:ss").toString(),
      callTypes: [
        this.$t("reports.filters.all"),
        this.$t("reports.filters.incoming"),
        this.$t("reports.filters.outgoing"),
      ],
      selectedCallType: 0,
      selectedAgent: 0,
      selectHaung: [
        this.$t("reports.filters.all"),
        this.$t("reports.filters.agent"),
        this.$t("reports.filters.caller"),
      ],
      selectedHaung: 0,
      selectedReport: 0,
      numberText: "",
      duration: "",
      periods: [
        { value: 2, label: this.$t("reports.filters.days") },
        { value: 1, label: this.$t("reports.filters.half_hours") },
      ],
      selectedSkill: 0,
      selectedPeriod: 2,
      agentStatusArr: [
        this.$t("reports.filters.all"),
        this.$t("reports.agent_status_report.status_online"),
        this.$t("reports.agent_status_report.status_rest"),
        this.$t("reports.agent_status_report.status_busy"),
        this.$t("reports.agent_status_report.status_offline"),
      ],
      selectedAgentStatus: 0,
      selectedOmniType: 0,
      selectedReportVariant: 'per-agent',
      reportVariantOptions: [
        { value: 'per-agent', label: this.$t("reports.csat_report.per_agent") },
        { value: 'per-month', label: this.$t("reports.csat_report.per_month") },
        { value: 'per-call', label: this.$t("reports.csat_report.per_call") },
      ],
      loading: false,
      loadingExport: false,
    };
  },
  computed: {
    ...mapGetters(["domain", "permissions", "aiReportsPermission"]),
    hasAiReportsPermission() {
      return this.userHasPermisson(this.permissions, this.aiReportsPermission);
    },
    selectReport() {
      const base = [
        { label: this.$t("reports.calls_report.title"), value: 0 },
        { label: this.$t("reports.performance_period_report.title"), value: 2 },
        { label: this.$t("reports.login_detail_report.title"), value: 6 },
        { label: this.$t("reports.login_summary_report.title"), value: 7 },
        { label: this.$t("reports.status_detail_report.title"), value: 8 },
        { label: this.$t("reports.status_summary_report.title"), value: 9 },
        { label: this.$t("reports.agent_kpi_report.title"), value: 10 },
        { label: this.$t("reports.csat_report.title"), value: 11 },
        { label: this.$t("reports.csat_table_report.title"), value: 15 },
        { label: this.$t("reports.repeated_calls_report.title"), value: 12 },
        { label: this.$t("reports.call_reasons_report.title"), value: 13 },
        // { label: this.$t("reports.ivr_report.title"), value: 14 },
      ];
      // AI reports are gated behind a dedicated permission.
      if (this.hasAiReportsPermission) {
        base.push(
          { label: this.$t("reports.voice_agent_cdr.title"), value: 17 },
          { label: this.$t("reports.ai_chat_transcript.title"), value: 18 }
        );
      }
      return base;
    },
    enableBtns() {
      return (
        this.frDate != "" &&
        moment(this.frDate, "YYYY-MM-DD HH:mm:ss", true).isValid() &&
        this.toDate != "" &&
        moment(this.toDate, "YYYY-MM-DD HH:mm:ss", true).isValid()
      );
    },
    filterCallType() {
      return this.showCallType;
    },
    filterAgent() {
      return this.showAgent;
    },
    filterHungparty() {
      return this.showHungparty;
    },
    filterNumber() {
      return this.showNumber;
    },
    filterDuration() {
      return this.showDuration;
    },
    filterSkills() {
      return this.showSkill;
    },
    filterPeriods() {
      return this.showPeriods;
    },
    filterAgentStatus() {
      return this.showAgentStatus;
    },
    NoExport() {
      return this.hideExport;
    },
    filterOmni() {
      return this.showOmni;
    },
    filterReportVariant() {
      return this.showReportVariant;
    },
    agentOptions() {
      return this.agents ? this.agents : [];
    },
    skillOptions() {
      return this.skills
        ? this.skills.map((skill) => ({
          value: skill.name,
          label:
            this.$i18n.locale === "ar" ? skill.titles.ar : skill.titles.en,
        }))
        : [];
    },
    callTypeOptions() {
      return [
        { value: 1, label: this.$t("reports.filters.incoming") },
        { value: 2, label: this.$t("reports.filters.outgoing") },
      ];
    },
    hungPartyOptions() {
      return [
        { value: 1, label: this.$t("reports.filters.agent") },
        { value: 2, label: this.$t("reports.filters.caller") },
      ];
    },
    agentStatusOptions() {
      return [
        {
          value: 1,
          label: this.$t("reports.agent_status_report.status_online"),
        },
        { value: 2, label: this.$t("reports.agent_status_report.status_rest") },
        { value: 3, label: this.$t("reports.agent_status_report.status_busy") },
        {
          value: 4,
          label: this.$t("reports.agent_status_report.status_offline"),
        },
      ];
    },
  },
  watch: {
    selectedReport() {
      // Reset all filters to defaults when report type changes
      this.resetFilters();
    },
  },
  methods: {
    resetFilters() {
      // Reset date to today
      this.frDate = moment().startOf("day").format("YYYY-MM-DD HH:mm:ss").toString();
      this.toDate = moment().endOf("day").format("YYYY-MM-DD HH:mm:ss").toString();
      this.selectedPreset = "today";

      // Reset all filter selections to defaults
      this.selectedCallType = 0;
      this.selectedAgent = 0;
      this.selectedHaung = 0;
      this.numberText = "";
      this.duration = "";
      this.selectedSkill = 0;
      this.selectedPeriod = 2;
      this.selectedAgentStatus = 0;
      this.selectedOmniType = 0;
      this.selectedReportVariant = 'per-agent';
    },
    async submit(_, isExport) {
      this.loading = !isExport;
      this.loadingExport = isExport;

      const filters = {
        from: moment(this.frDate, "YYYY-MM-DD HH:mm:ss", true).format(
          "YYYY-MM-DD HH:mm:ss"
        ),
        to: moment(this.toDate, "YYYY-MM-DD HH:mm:ss", true).format(
          "YYYY-MM-DD HH:mm:ss"
        ),
        callType: this.filterCallType ? this.selectedCallType : null,
        agent: this.filterAgent ? this.selectedAgent : null,
        hungparty: this.filterHungparty ? this.selectedHaung : null,
        number: this.filterNumber ? this.numberText : null,
        duration:
          this.filterDuration && this.duration != ""
            ? moment(this.duration).format("HH:mm:ss")
            : "",
        skill: this.filterSkills
          ? this.selectedSkill !== null && this.selectedSkill !== undefined
            ? this.selectedSkill
            : 0
          : 0,
        period: this.filterPeriods ? this.selectedPeriod : null,
        agentStatus: this.filterAgentStatus ? this.selectedAgentStatus : null,
        omniType: this.filterOmni ? this.selectedOmniType : null,
        reportVariant: this.filterReportVariant ? this.selectedReportVariant : null,
        export: isExport,
        report: this.selectedReport,
      };

      this.$emit("submitReport", filters);
      this.loading = false;
      this.loadingExport = false;
    },

    // Event handlers for filter changes
    handlePresetChange(presetKey) {
      if (this.$refs.quickDates) {
        const { from, to } = this.$refs.quickDates.getPresetDates(presetKey);
        this.frDate = from;
        this.toDate = to;
      }
      // Don't auto-submit when preset changes to avoid unnecessary reloads
    },

    handleSingleDayPresetChange(presetKey) {
      // Notify the date picker component to change the date
      if (this.$refs.singleDayDateRange) {
        this.$refs.singleDayDateRange.setDate(presetKey);
      }
      // Don't auto-submit when preset changes to avoid unnecessary reloads
    },

    handleReportChange(value) {
      const idx = value;
      if (idx !== -1) {
        this.$emit("report_type", idx);
      }
      // Don't auto-submit to prevent unnecessary reloads
      // The parent component will handle data fetching
    },

    handleDateChange(dates) {
      this.frDate = dates.from;
      this.toDate = dates.to;
    },

    handleDurationChange(value) {
      this.duration = value;
    },

    handleCallTypeChange(value) {
      this.selectedCallType = value;
    },

    handleAgentChange(value) {
      this.selectedAgent = value;
    },

    handleHungPartyChange(value) {
      this.selectedHaung = value;
    },

    handleNumberChange(value) {
      this.numberText = value;
    },

    handleSkillChange(value) {
      this.selectedSkill = value;
      this.$emit("skillChanged", value);
    },

    handlePeriodChange(value) {
      this.selectedPeriod = value;
    },

    handleAgentStatusChange(value) {
      this.selectedAgentStatus = value;
    },

    handleOmniChange(value) {
      this.selectedOmniType = value;
    },

    handleReportVariantChange(value) {
      this.selectedReportVariant = value;
    },
  },
};
</script>

<style>
.dp__theme_light {
  --dp-background-color: #fff;
  --dp-text-color: #212121;
  --dp-hover-color: #f3f3f3;
  --dp-hover-text-color: #212121;
  --dp-hover-icon-color: #959595;
  --dp-primary-color: var(--p-primary-500) !important;
  --dp-border-color: white !important;
  --dp-menu-border-color: white !important;
  --dp-border-color-hover: white !important;
  --dp-border-color-focus: white !important;
  --dp-disabled-color: #f6f6f6;
}

::placeholder {
  color: black;
  opacity: 1;
  padding-left: 6px;
}

::-ms-input-placeholder {
  color: black;
}

.dp__instance_calendar {
  background: white;
}
</style>
